import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export enum RoomBookingPageMode {
  Map = 'map',
  List = 'list',
}

export interface RoomBookingPageModeContextValue {
  mode: RoomBookingPageMode | null;
  setMode: (mode: RoomBookingPageMode) => void;
}

export const RoomBookingPageModeContext = createContext<
  RoomBookingPageModeContextValue | undefined
>(undefined);
RoomBookingPageModeContext.displayName = 'RoomBookingPageModeContext';

interface RoomBookingPageModeProviderProps {
  children?: ReactNode;
}

export const RoomBookingPageModeProvider: FC<
  RoomBookingPageModeProviderProps
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get('mode') as RoomBookingPageMode | null;

  const setMode = useCallback(
    (value: RoomBookingPageMode) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('mode', value);
        return next;
      });
      dispatch(sidesheetActions.close());
    },
    [dispatch, setSearchParams],
  );

  useEffect(() => {
    if (!mode) {
      setMode(RoomBookingPageMode.Map);
    }
  }, [mode, setMode]);

  const contextValue = useMemo((): RoomBookingPageModeContextValue => {
    return {
      mode,
      setMode,
    };
  }, [mode, setMode]);

  return (
    <RoomBookingPageModeContext.Provider value={contextValue}>
      {children}
    </RoomBookingPageModeContext.Provider>
  );
};

export const useRoomBookingPageMode = (): RoomBookingPageModeContextValue =>
  useRequiredContext(RoomBookingPageModeContext);
